import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useExpense } from '../../context/ExpenseContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { format, parse, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Plus, Search, MoreHorizontal, Pencil, Trash2, Copy, Download, Upload, 
  Calendar as CalendarIcon, Loader2, X, FileCheck, FileWarning,
  AlertTriangle, CheckCircle2, XCircle, Info, FileSpreadsheet
} from 'lucide-react';

const formatCurrency = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

// Date preset options
const DATE_PRESETS = [
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'currentMonth', label: 'Current Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'currentYear', label: 'Current Year' },
  { value: 'lastYear', label: 'Last Year' }
];

// Helper function to calculate date range for presets
const getDateRangeForPreset = (preset) => {
  const today = new Date();
  switch (preset) {
    case 'last30':
      return { start: subDays(today, 30), end: today };
    case 'currentMonth':
      return { start: startOfMonth(today), end: endOfMonth(today) };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    case 'currentYear':
      return { start: startOfYear(today), end: endOfYear(today) };
    case 'lastYear':
      const lastYear = subYears(today, 1);
      return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
    default:
      return null;
  }
};

// Field order for import/export and form
const FIELD_ORDER = [
  { key: 'date', label: 'Date (MM-DD-YYYY)', required: true },
  { key: 'item_name', label: 'Name', required: true },
  { key: 'description', label: 'Description', required: false },
  { key: 'category', label: 'Category', required: true, isLOV: true },
  { key: 'amount', label: 'Amount', required: true },
  { key: 'currency', label: 'Currency', required: false },
  { key: 'payment_mode', label: 'Payment Mode', required: true, isLOV: true },
  { key: 'paid_by', label: 'Payment Made By', required: true, isLOV: true },
  { key: 'has_invoice', label: 'Invoice Saved?', required: false },
  { key: 'notes', label: 'Remarks', required: false }
];

const ExpenseList = () => {
  const {
    expenses, loading, pagination, lov, fetchExpenses, fetchLOV, createExpense, updateExpense, 
    deleteExpense, duplicateExpense, importExpenses, exportExpenses, bulkDelete, isReady
  } = useExpense();
  
  const fileInputRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'preset', 'custom'
  const [datePreset, setDatePreset] = useState(''); // 'last30', 'currentMonth', 'lastMonth', 'currentYear', 'lastYear'
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  // Import preview data
  const [importPreviewData, setImportPreviewData] = useState({ valid: [], invalid: [], file: null });
  const [importStep, setImportStep] = useState('upload'); // 'instructions', 'upload', 'preview', 'importing'
  const [importProgress, setImportProgress] = useState({ phase: '', percent: 0 });
  
  // Bulk delete
  const [bulkDeleteRange, setBulkDeleteRange] = useState({ start: '', end: '' });
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  
  // Form data - following the required field order
  const [formData, setFormData] = useState({
    date: '',
    item_name: '',
    description: '',
    category: '',
    amount: '',
    currency: 'INR',
    payment_mode: '',
    paid_by: '',
    has_invoice: false,
    notes: ''
  });

  // Bug 5 Fix: Ensure expenses are loaded on initial render when auth is ready
  useEffect(() => {
    if (isReady) {
      loadExpensesWithFilters();
      fetchLOV();
    }
  }, [isReady]);

  // Reload LOV when dialog opens
  useEffect(() => {
    if (createDialogOpen || editDialogOpen) {
      fetchLOV();
    }
  }, [createDialogOpen, editDialogOpen, fetchLOV]);

  // Calculate effective date range based on filter type
  const getEffectiveDateRange = (filterType, preset, customRange) => {
    if (filterType === 'preset' && preset) {
      return getDateRangeForPreset(preset);
    } else if (filterType === 'custom' && customRange.start && customRange.end) {
      return customRange;
    }
    return null;
  };

  const loadExpensesWithFilters = async (page = currentPage) => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (categoryFilter && categoryFilter !== 'all') filters.category = categoryFilter;
    
    // Apply date range filter
    const dateRange = getEffectiveDateRange(dateFilterType, datePreset, customDateRange);
    if (dateRange) {
      filters.start_date = format(dateRange.start, 'yyyy-MM-dd');
      filters.end_date = format(dateRange.end, 'yyyy-MM-dd');
    }
    
    await fetchExpenses(filters, page, pageSize);
  };

  // Stringify customDateRange for dependency tracking
  const customDateRangeKey = customDateRange.start && customDateRange.end 
    ? `${customDateRange.start.toISOString()}-${customDateRange.end.toISOString()}` 
    : '';

  useEffect(() => {
    if (!isReady) return;
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on filter change
      loadExpensesWithFilters(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, categoryFilter, dateFilterType, datePreset, customDateRangeKey, isReady]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadExpensesWithFilters(newPage);
  };

  // Handle date filter type change
  const handleDateFilterTypeChange = (type) => {
    setDateFilterType(type);
    if (type === 'all') {
      setDatePreset('');
      setCustomDateRange({ start: null, end: null });
    }
  };

  // Handle preset change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    setDateFilterType('preset');
  };

  const resetForm = () => {
    setFormData({
      date: '',
      item_name: '',
      description: '',
      category: '',
      amount: '',
      currency: 'INR',
      payment_mode: '',
      paid_by: '',
      has_invoice: false,
      notes: ''
    });
    setFormErrors({});
    setSelectedExpense(null);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.date) {
      errors.date = 'Date is required';
    } else {
      // Check if date is in the future - normalize both dates to midnight for comparison
      const selectedDate = new Date(formData.date + 'T00:00:00');
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today to allow today's date
      if (selectedDate > today) {
        errors.date = 'Future dates are not allowed';
      }
    }
    
    if (!formData.item_name?.trim()) {
      errors.item_name = 'Name is required';
    }
    
    // Bug 4 Fix: Enhanced amount validation
    if (!formData.amount || formData.amount === '') {
      errors.amount = 'Amount is required';
    } else {
      const amountNum = parseFloat(formData.amount);
      if (isNaN(amountNum)) {
        errors.amount = 'Please enter a valid numeric amount';
      } else if (amountNum <= 0) {
        errors.amount = 'Amount must be greater than 0';
      }
    }
    
    // LOV Validation
    if (!formData.category) {
      errors.category = 'Category is required';
    } else if (lov.categories.length > 0 && !lov.categories.includes(formData.category)) {
      errors.category = 'Please select a valid category from the list';
    }
    
    if (!formData.payment_mode) {
      errors.payment_mode = 'Payment Mode is required';
    } else if (lov.payment_modes.length > 0 && !lov.payment_modes.includes(formData.payment_mode)) {
      errors.payment_mode = 'Please select a valid payment mode from the list';
    }
    
    if (!formData.paid_by) {
      errors.paid_by = 'Payment Made By is required';
    } else if (lov.payers.length > 0 && !lov.payers.includes(formData.paid_by)) {
      errors.paid_by = 'Please select a valid payer from the list';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    setFormLoading(true);
    const result = await createExpense({
      ...formData,
      amount: parseFloat(formData.amount)
    });
    setFormLoading(false);
    
    if (result.success) {
      toast.success('Expense created successfully');
      setCreateDialogOpen(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedExpense) return;
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    setFormLoading(true);
    const result = await updateExpense(selectedExpense.id, {
      ...formData,
      amount: parseFloat(formData.amount)
    });
    setFormLoading(false);
    
    if (result.success) {
      toast.success('Expense updated successfully');
      setEditDialogOpen(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    
    setFormLoading(true);
    const result = await deleteExpense(selectedExpense.id);
    setFormLoading(false);
    
    if (result.success) {
      toast.success('Expense deleted');
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
    } else {
      toast.error(result.error);
    }
  };

  const handleDuplicate = async (expense) => {
    const result = await duplicateExpense(expense.id);
    if (result.success) {
      toast.success('Expense duplicated');
    } else {
      toast.error(result.error);
    }
  };

  const openEditDialog = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      date: expense.date,
      item_name: expense.item_name,
      description: expense.description || '',
      category: expense.category,
      amount: String(expense.amount),
      currency: expense.currency,
      payment_mode: expense.payment_mode,
      paid_by: expense.paid_by,
      has_invoice: expense.has_invoice,
      notes: expense.notes || ''
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  // Normalize header to consistent key format (matches backend logic)
  const normalizeHeader = (header) => {
    if (!header) return '';
    let h = String(header).trim().toLowerCase();
    // Remove special chars but keep underscores
    h = h.replace(/[()?\-\/]/g, '');
    // Replace multiple spaces with single underscore
    h = h.split(/\s+/).join('_');
    // Remove trailing underscores
    h = h.replace(/_+$/, '');
    return h;
  };

  // Header aliases mapping (matches backend)
  const headerAliases = {
    date: ['date_mmddyyyy', 'date'],
    item_name: ['name', 'item_name', 'expense_name'],
    description: ['description', 'desc'],
    category: ['category', 'cat'],
    amount: ['amount', 'amt', 'value'],
    currency: ['currency', 'curr'],
    payment_mode: ['payment_mode', 'paymentmode', 'mode'],
    paid_by: ['payment_made_by', 'paymentmadeby', 'paid_by', 'paidby', 'payer'],
    has_invoice: ['invoice_saved', 'invoicesaved', 'has_invoice', 'hasinvoice', 'invoice'],
    notes: ['remarks', 'notes', 'comment', 'comments']
  };

  // Parse XLSX file for preview
  const parseXLSXFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            defval: ''
          });
          
          if (jsonData.length === 0) {
            reject(new Error('Excel file must have at least one data row'));
            return;
          }
          
          // Get normalized header map
          const rawHeaders = Object.keys(jsonData[0] || {});
          const normalizedHeaders = rawHeaders.map(h => normalizeHeader(h));
          
          // Find column for a field using aliases
          const findColumn = (fieldName) => {
            const aliases = headerAliases[fieldName] || [fieldName];
            for (const alias of aliases) {
              const idx = normalizedHeaders.indexOf(alias);
              if (idx >= 0) return rawHeaders[idx];
            }
            return null;
          };
          
          // Map fields to actual column names
          const colMap = {};
          Object.keys(headerAliases).forEach(field => {
            colMap[field] = findColumn(field);
          });
          
          // Normalize and map records
          const records = jsonData.map((row, idx) => {
            const getValue = (field) => {
              const col = colMap[field];
              if (!col) return '';
              let val = row[col];
              if (val === null || val === undefined) return '';
              // Handle Date objects from xlsx
              if (val instanceof Date) {
                return val.toISOString().split('T')[0];
              }
              val = String(val).trim();
              if (val.toLowerCase() === 'none') return '';
              return val;
            };
            
            return {
              _row: idx + 2,
              date: getValue('date'),
              item_name: getValue('item_name'),
              description: getValue('description'),
              category: getValue('category'),
              amount: getValue('amount'),
              currency: getValue('currency') || 'INR',
              payment_mode: getValue('payment_mode'),
              paid_by: getValue('paid_by'),
              has_invoice: ['true', 'yes', '1', 'y'].includes(getValue('has_invoice').toLowerCase()),
              notes: getValue('notes')
            };
          });
          
          resolve(records);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Bug 7 Fix: Case-insensitive validation for import records against LOV
  const validateImportRecord = (record) => {
    const errors = [];
    
    // Required fields (record already has normalized field names)
    if (!record.date) {
      errors.push('Date is required');
    } else {
      // Date format validation - accepting MM-DD-YYYY, MM/DD/YYYY, and YYYY-MM-DD formats
      const dateStr = String(record.date).trim();
      const mmddyyyyRegex = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/;
      const yyyymmddRegex = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
      const mmddMatch = dateStr.match(mmddyyyyRegex);
      const yyyyMatch = dateStr.match(yyyymmddRegex);
      
      if (!mmddMatch && !yyyyMatch) {
        errors.push(`Invalid date format: "${dateStr}". Expected format: MM-DD-YYYY`);
      } else {
        let month, day, year;
        if (yyyyMatch) {
          year = parseInt(yyyyMatch[1], 10);
          month = parseInt(yyyyMatch[2], 10);
          day = parseInt(yyyyMatch[3], 10);
        } else {
          month = parseInt(mmddMatch[1], 10);
          day = parseInt(mmddMatch[2], 10);
          year = parseInt(mmddMatch[3], 10);
        }
        
        // Validate month and day ranges
        if (month < 1 || month > 12) {
          errors.push(`Invalid month in date: "${dateStr}". Month must be 1-12`);
        } else if (day < 1 || day > 31) {
          errors.push(`Invalid day in date: "${dateStr}". Day must be 1-31`);
        } else {
          // Create date object and validate it's a real date
          const parsedDate = new Date(year, month - 1, day);
          
          if (parsedDate.getMonth() !== month - 1 || parsedDate.getDate() !== day) {
            errors.push(`Invalid date: "${dateStr}". This date does not exist`);
          } else {
            // Check for future dates
            const today = new Date();
            today.setHours(23, 59, 59, 999); // End of today
            
            if (parsedDate > today) {
              errors.push(`Future date not allowed: "${dateStr}". Date cannot be in the future`);
            }
          }
        }
      }
    }
    
    if (!record.item_name) errors.push('Name is required');
    if (!record.amount || isNaN(parseFloat(record.amount))) errors.push('Valid amount is required');
    
    // Helper for case-insensitive matching
    const findCaseInsensitiveMatch = (value, list) => {
      if (!value || !list || list.length === 0) return null;
      const lowerValue = value.toLowerCase().trim();
      return list.find(item => item.toLowerCase().trim() === lowerValue);
    };
    
    // LOV validation - Bug 7 Fix: Case-insensitive matching
    if (!record.category) {
      errors.push('Category is required');
    } else {
      const matchedCategory = findCaseInsensitiveMatch(record.category, lov.categories);
      if (lov.categories.length > 0 && !matchedCategory) {
        errors.push(`Invalid category: "${record.category}". Valid options: ${lov.categories.join(', ')}`);
      } else if (matchedCategory) {
        // Update record with correct case
        record.category = matchedCategory;
      }
    }
    
    if (!record.payment_mode) {
      errors.push('Payment Mode is required');
    } else {
      const matchedPaymentMode = findCaseInsensitiveMatch(record.payment_mode, lov.payment_modes);
      if (lov.payment_modes.length > 0 && !matchedPaymentMode) {
        errors.push(`Invalid payment mode: "${record.payment_mode}". Valid options: ${lov.payment_modes.join(', ')}`);
      } else if (matchedPaymentMode) {
        record.payment_mode = matchedPaymentMode;
      }
    }
    
    if (!record.paid_by) {
      errors.push('Payment Made By is required');
    } else {
      const matchedPayer = findCaseInsensitiveMatch(record.paid_by, lov.payers);
      if (lov.payers.length > 0 && !matchedPayer) {
        errors.push(`Invalid payer: "${record.paid_by}". Valid options: ${lov.payers.join(', ')}`);
      } else if (matchedPayer) {
        record.paid_by = matchedPayer;
      }
    }
    
    return errors;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Reset file input
    e.target.value = '';
    
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported. Please save your file as .xlsx format in Excel.');
      return;
    }
    
    try {
      // Ensure LOV is loaded
      await fetchLOV();
      
      const records = await parseXLSXFile(file);
      
      const valid = [];
      const invalid = [];
      
      records.forEach(record => {
        const errors = validateImportRecord(record);
        if (errors.length === 0) {
          valid.push(record);
        } else {
          invalid.push({ ...record, _errors: errors });
        }
      });
      
      setImportPreviewData({ valid, invalid, file });
      setImportStep('preview');
      setImportPreviewOpen(true);
    } catch (err) {
      toast.error(err.message || 'Failed to parse file');
    }
  };

  const handleConfirmImport = async () => {
    if (importPreviewData.valid.length === 0) {
      toast.error('No valid records to import');
      return;
    }
    
    setImportStep('importing');
    setFormLoading(true);
    setImportProgress({ phase: 'Preparing records...', percent: 10 });
    
    // Simulate progress phases while the actual import happens
    const progressTimer = setTimeout(() => {
      setImportProgress({ phase: 'Sending records to server...', percent: 30 });
    }, 400);
    
    const progressTimer2 = setTimeout(() => {
      setImportProgress({ phase: 'Validating records...', percent: 55 });
    }, 900);
    
    const progressTimer3 = setTimeout(() => {
      setImportProgress({ phase: 'Saving to database...', percent: 75 });
    }, 1500);
    
    // Map validated preview records to the import format
    const records = importPreviewData.valid.map(r => ({
      date: r.date || '',
      item_name: r.item_name || '',
      description: r.description || '',
      category: r.category || '',
      amount: parseFloat(r.amount) || 0,
      currency: r.currency || 'INR',
      payment_mode: r.payment_mode || '',
      paid_by: r.paid_by || '',
      has_invoice: String(r.has_invoice || '').toLowerCase() === 'yes' || String(r.has_invoice || '').toLowerCase() === 'true',
      notes: r.notes || ''
    }));
    
    const result = await importExpenses(records);
    
    clearTimeout(progressTimer);
    clearTimeout(progressTimer2);
    clearTimeout(progressTimer3);
    
    if (result.success) {
      setImportProgress({ phase: 'Import complete!', percent: 100 });
      await new Promise(r => setTimeout(r, 600));
      setFormLoading(false);
      toast.success(`Imported ${result.data.valid_count} expenses`);
      if (result.data.invalid_count > 0) {
        toast.warning(`${result.data.invalid_count} records were skipped`);
      }
      setImportPreviewOpen(false);
      setImportPreviewData({ valid: [], invalid: [], file: null });
      setImportStep('upload');
      setImportProgress({ phase: '', percent: 0 });
    } else {
      setFormLoading(false);
      toast.error(result.error);
      setImportStep('preview');
      setImportProgress({ phase: '', percent: 0 });
    }
  };

  const handleExport = async () => {
    const result = await exportExpenses();
    if (result.success) {
      toast.success('Export downloaded');
    } else {
      toast.error(result.error);
    }
  };

  const handleBulkDelete = async (deleteAll = false) => {
    if (deleteAll && !deleteAllConfirm) {
      setDeleteAllConfirm(true);
      return;
    }
    
    setFormLoading(true);
    const result = await bulkDelete(
      deleteAll ? null : bulkDeleteRange.start,
      deleteAll ? null : bulkDeleteRange.end,
      deleteAll
    );
    setFormLoading(false);
    
    if (result.success) {
      toast.success(result.message);
      setBulkDeleteOpen(false);
      setBulkDeleteRange({ start: '', end: '' });
      setDeleteAllConfirm(false);
    } else {
      toast.error(result.error);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setDateFilterType('all');
    setDatePreset('');
    setCustomDateRange({ start: null, end: null });
  };

  // Get display text for current date filter
  const getDateFilterDisplayText = () => {
    if (dateFilterType === 'preset' && datePreset) {
      const preset = DATE_PRESETS.find(p => p.value === datePreset);
      return preset ? preset.label : 'Select period';
    } else if (dateFilterType === 'custom' && customDateRange.start && customDateRange.end) {
      return `${format(customDateRange.start, 'MM-dd-yyyy')} to ${format(customDateRange.end, 'MM-dd-yyyy')}`;
    }
    return 'All Time';
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return format(date, 'MM-dd-yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="expense-list">
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2" data-testid="add-expense-btn">
            <Plus className="w-4 h-4" />
            Add Expense
          </Button>
          {/* Bug 6 Fix: Add instructions button before import */}
          <Button 
            variant="outline" 
            onClick={() => {
              setImportPreviewData({ valid: [], invalid: [], file: null });
              setImportStep('instructions');
              setImportPreviewOpen(true);
            }} 
            className="gap-2"
            data-testid="import-excel-btn"
          >
            <Upload className="w-4 h-4" />
            Import Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
          <Button 
            variant="outline" 
            className="gap-2 text-destructive hover:text-destructive"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-4 h-4" />
            Bulk Delete
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-muted-foreground text-sm">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-expenses"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-muted-foreground text-sm">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {lov.categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Enhanced Date Filter */}
            <div className="w-[280px]">
              <Label className="text-muted-foreground text-sm">Date Period</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full mt-1 justify-start font-normal text-left"
                    data-testid="date-filter-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{getDateFilterDisplayText()}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-4 space-y-4">
                    {/* Preset Options */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Quick Select</Label>
                      <div className="grid grid-cols-1 gap-1">
                        <Button
                          variant={dateFilterType === 'all' ? 'secondary' : 'ghost'}
                          size="sm"
                          className="justify-start"
                          onClick={() => handleDateFilterTypeChange('all')}
                        >
                          All Time
                        </Button>
                        {DATE_PRESETS.map(preset => (
                          <Button
                            key={preset.value}
                            variant={dateFilterType === 'preset' && datePreset === preset.value ? 'secondary' : 'ghost'}
                            size="sm"
                            className="justify-start"
                            onClick={() => handlePresetChange(preset.value)}
                            data-testid={`date-preset-${preset.value}`}
                          >
                            {preset.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <Separator />
                    
                    {/* Custom Date Range */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Custom Range</Label>
                      <div className="flex gap-2 items-center">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-[120px] justify-start font-normal text-xs"
                            >
                              {customDateRange.start 
                                ? format(customDateRange.start, 'MM-dd-yyyy') 
                                : 'Start date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={customDateRange.start}
                              onSelect={(date) => {
                                setCustomDateRange(prev => ({ ...prev, start: date }));
                                if (date && customDateRange.end) {
                                  setDateFilterType('custom');
                                }
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <span className="text-muted-foreground text-xs">to</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-[120px] justify-start font-normal text-xs"
                            >
                              {customDateRange.end 
                                ? format(customDateRange.end, 'MM-dd-yyyy') 
                                : 'End date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={customDateRange.end}
                              onSelect={(date) => {
                                setCustomDateRange(prev => ({ ...prev, end: date }));
                                if (customDateRange.start && date) {
                                  setDateFilterType('custom');
                                }
                              }}
                              disabled={(date) => customDateRange.start && date < customDateRange.start}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      {customDateRange.start && customDateRange.end && (
                        <Button
                          variant={dateFilterType === 'custom' ? 'secondary' : 'outline'}
                          size="sm"
                          className="w-full"
                          onClick={() => setDateFilterType('custom')}
                        >
                          Apply Custom Range
                        </Button>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {(searchQuery || categoryFilter !== 'all' || dateFilterType !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Expenses</span>
            <Badge variant="secondary">{pagination?.total || (Array.isArray(expenses) ? expenses.length : 0)} records</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !Array.isArray(expenses) || expenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No expenses found. Add your first expense to get started.
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name / Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense?.id || Math.random()} data-testid={`expense-row-${expense?.id}`}>
                      <TableCell className="whitespace-nowrap">{formatDisplayDate(expense?.date)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{expense?.item_name || 'N/A'}</p>
                          {expense?.description && (
                            <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                              {expense.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{expense?.category || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap">
                        {formatCurrency(expense?.amount || 0, expense?.currency)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{expense.payment_mode}</p>
                          <p className="text-xs text-muted-foreground">{expense.paid_by}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {expense.has_invoice ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Yes</Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(expense)}>
                              <Pencil className="mr-2 w-4 h-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(expense)}>
                              <Copy className="mr-2 w-4 h-4" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => {
                                setSelectedExpense(expense);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 w-4 h-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {!loading && Array.isArray(expenses) && expenses.length > 0 && pagination?.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total} records
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-2">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog - Updated field order */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDialogOpen ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            <DialogDescription>
              {editDialogOpen ? 'Update expense details' : 'Enter expense details. Fields marked with * are required.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* 1. Date - Bug 2 Fix: Simplified label */}
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={formErrors.date ? 'border-destructive' : ''}
                data-testid="expense-date"
              />
              {formErrors.date && (
                <p className="text-xs text-destructive">{formErrors.date}</p>
              )}
            </div>
            
            {/* 2. Name */}
            <div className="space-y-2">
              <Label htmlFor="item_name">Name *</Label>
              <Input
                id="item_name"
                placeholder="Enter expense name"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                className={formErrors.item_name ? 'border-destructive' : ''}
                data-testid="expense-item"
              />
              {formErrors.item_name && (
                <p className="text-xs text-destructive">{formErrors.item_name}</p>
              )}
            </div>
            
            {/* 3. Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* 4. Category */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger 
                  className={formErrors.category ? 'border-destructive' : ''} 
                  data-testid="expense-category"
                >
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {lov.categories.length === 0 ? (
                    <SelectItem value="_loading" disabled>Loading categories...</SelectItem>
                  ) : (
                    lov.categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formErrors.category && (
                <p className="text-xs text-destructive">{formErrors.category}</p>
              )}
            </div>
            
            {/* 5. Amount with Currency - Bug 4 Fix: Better validation for numeric input */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Bug 4 Fix: Only allow valid numeric values
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setFormData({ ...formData, amount: val });
                    }
                  }}
                  onKeyDown={(e) => {
                    // Bug 4 Fix: Prevent non-numeric characters
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className={formErrors.amount ? 'border-destructive' : ''}
                  data-testid="expense-amount"
                />
                {formErrors.amount && (
                  <p className="text-xs text-destructive">{formErrors.amount}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* 6. Payment Mode */}
            <div className="space-y-2">
              <Label>Payment Mode *</Label>
              <Select 
                value={formData.payment_mode} 
                onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}
              >
                <SelectTrigger 
                  className={formErrors.payment_mode ? 'border-destructive' : ''} 
                  data-testid="expense-payment"
                >
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  {lov.payment_modes.length === 0 ? (
                    <SelectItem value="_loading" disabled>Loading payment modes...</SelectItem>
                  ) : (
                    lov.payment_modes.map(mode => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formErrors.payment_mode && (
                <p className="text-xs text-destructive">{formErrors.payment_mode}</p>
              )}
            </div>
            
            {/* 7. Payment Made By */}
            <div className="space-y-2">
              <Label>Payment Made By *</Label>
              <Select 
                value={formData.paid_by} 
                onValueChange={(v) => setFormData({ ...formData, paid_by: v })}
              >
                <SelectTrigger 
                  className={formErrors.paid_by ? 'border-destructive' : ''} 
                  data-testid="expense-payer"
                >
                  <SelectValue placeholder="Select payer" />
                </SelectTrigger>
                <SelectContent>
                  {lov.payers.length === 0 ? (
                    <SelectItem value="_loading" disabled>Loading payers...</SelectItem>
                  ) : (
                    lov.payers.map(payer => (
                      <SelectItem key={payer} value={payer}>{payer}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formErrors.paid_by && (
                <p className="text-xs text-destructive">{formErrors.paid_by}</p>
              )}
            </div>
            
            {/* 8. Invoice Saved? */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has_invoice"
                checked={formData.has_invoice}
                onCheckedChange={(checked) => setFormData({ ...formData, has_invoice: checked })}
              />
              <Label htmlFor="has_invoice" className="font-normal cursor-pointer">
                Invoice Saved?
              </Label>
            </div>
            
            {/* 9. Remarks */}
            <div className="space-y-2">
              <Label htmlFor="notes">Remarks</Label>
              <Textarea
                id="notes"
                placeholder="Additional remarks (optional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={editDialogOpen ? handleUpdate : handleCreate} 
              disabled={formLoading}
              data-testid="save-expense-btn"
            >
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editDialogOpen ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
            <DialogDescription className="break-words">
              Are you sure you want to delete "<span className="font-medium break-all">{selectedExpense?.item_name?.length > 100 ? selectedExpense?.item_name?.substring(0, 100) + '...' : selectedExpense?.item_name}</span>"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Preview Dialog - Bug 6 Fix: Added instructions step */}
      <Dialog open={importPreviewOpen} onOpenChange={(open) => {
        if (!open && importStep !== 'importing') {
          setImportPreviewOpen(false);
          setImportPreviewData({ valid: [], invalid: [], file: null });
          setImportStep('upload');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {importStep === 'importing' ? 'Importing...' : 'Import Expenses from Excel'}
            </DialogTitle>
            <DialogDescription>
              {importStep === 'instructions' 
                ? 'Review the import instructions before uploading your file.'
                : importStep === 'importing'
                ? 'Please wait while your records are being imported.'
                : 'Review the data before importing. Records with validation errors will be skipped.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Bug 6 Fix: Instructions Step */}
          {importStep === 'instructions' && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  Import Instructions
                </h4>
                <div className="space-y-3 text-sm text-blue-800">
                  <div>
                    <p className="font-medium">Expected Column Headers (in order):</p>
                    <p className="text-xs mt-1 bg-white/50 p-2 rounded">
                      {FIELD_ORDER.map(f => f.label).join(' | ')}
                    </p>
                  </div>
                  <Separator className="bg-blue-200" />
                  <div>
                    <p className="font-medium text-red-600">Required Fields:</p>
                    <ul className="list-disc list-inside text-xs mt-1">
                      <li>Date (MM-DD-YYYY format)</li>
                      <li>Name (expense item name)</li>
                      <li>Category (must match values in Settings)</li>
                      <li>Amount (numeric value)</li>
                      <li>Payment Mode (must match values in Settings)</li>
                      <li>Payment Made By (must match values in Settings)</li>
                    </ul>
                  </div>
                  <Separator className="bg-blue-200" />
                  <div>
                    <p className="font-medium">Important Notes:</p>
                    <ul className="list-disc list-inside text-xs mt-1">
                      <li>Category, Payment Mode, and Payer values are validated against Settings (case-insensitive)</li>
                      <li>Invalid records will be skipped during import</li>
                      <li>Download the sample template for the correct format</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    // Download sample template
                    try {
                      // axios, so the app's interceptor adds a live token
                      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/import-template`, {
                        responseType: 'blob'
                      });
                      if (response.status === 200) {
                        const blob = response.data;
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'expense_import_template.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('Template downloaded');
                      } else {
                        toast.error('Failed to download template');
                      }
                    } catch {
                      toast.error('Failed to download template');
                    }
                  }}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Sample Template
                </Button>
                <Button 
                  onClick={() => {
                    setImportStep('upload');
                    fileInputRef.current?.click();
                  }}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Select Excel File
                </Button>
              </div>
            </div>
          )}
          
          {/* Preview Step */}
          {importStep === 'preview' && (
          <div className="space-y-4 py-4">
            {/* Field Order Reference */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <Info className="w-4 h-4 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Expected Excel Column Order:</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {FIELD_ORDER.map(f => f.label).join(', ')}
                  </p>
                </div>
              </div>
              <Separator className="my-2" />
              <p className="text-xs text-muted-foreground">
                <strong>Required fields:</strong> Date, Name, Category, Amount, Payment Mode, Payment Made By
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>LOV Validated:</strong> Category, Payment Mode, and Payment Made By must match values in Settings (case-insensitive)
              </p>
            </div>
            
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-2xl font-bold text-green-700">{importPreviewData.valid.length}</p>
                      <p className="text-sm text-green-600">Valid Records</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-8 h-8 text-red-600" />
                    <div>
                      <p className="text-2xl font-bold text-red-700">{importPreviewData.invalid.length}</p>
                      <p className="text-sm text-red-600">Invalid Records</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Valid Records Preview */}
            {importPreviewData.valid.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-green-600" />
                  Valid Records (will be imported)
                </h4>
                <div className="rounded-md border max-h-[200px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Payment Mode</TableHead>
                        <TableHead>Paid By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreviewData.valid.slice(0, 10).map((record, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{record._row}</TableCell>
                          <TableCell>{record.date}</TableCell>
                          <TableCell>{record.item_name}</TableCell>
                          <TableCell>{record.category}</TableCell>
                          <TableCell>{record.amount}</TableCell>
                          <TableCell>{record.payment_mode}</TableCell>
                          <TableCell>{record.paid_by}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {importPreviewData.valid.length > 10 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Showing first 10 of {importPreviewData.valid.length} valid records
                  </p>
                )}
              </div>
            )}
            
            {/* Invalid Records Preview */}
            {importPreviewData.invalid.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2 text-destructive">
                  <FileWarning className="w-4 h-4" />
                  Invalid Records (will be skipped)
                </h4>
                <div className="rounded-md border border-destructive/30 max-h-[200px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreviewData.invalid.slice(0, 10).map((record, idx) => (
                        <TableRow key={idx} className="bg-red-50/50">
                          <TableCell>{record._row}</TableCell>
                          <TableCell>{record.date || '-'}</TableCell>
                          <TableCell>{record.item_name || record.name || '-'}</TableCell>
                          <TableCell>
                            <ul className="text-xs text-destructive list-disc list-inside">
                              {record._errors?.map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {importPreviewData.invalid.length > 10 && (
                  <p className="text-xs text-destructive mt-1">
                    Showing first 10 of {importPreviewData.invalid.length} invalid records
                  </p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Importing Progress UI */}
          {importStep === 'importing' && (
          <div className="py-8 flex flex-col items-center gap-6" data-testid="import-progress">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
                <circle
                  cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="6"
                  className="text-primary transition-all duration-500 ease-out"
                  strokeDasharray={`${2 * Math.PI * 34}`}
                  strokeDashoffset={`${2 * Math.PI * 34 * (1 - importProgress.percent / 100)}`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                {importProgress.percent}%
              </span>
            </div>
            <div className="text-center space-y-1.5">
              <p className="text-sm font-medium">{importProgress.phase}</p>
              <p className="text-xs text-muted-foreground">
                Importing {importPreviewData.valid.length} record{importPreviewData.valid.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          )}
          
          {/* Dialog Footer for preview step */}
          {importStep === 'preview' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportPreviewOpen(false);
              setImportPreviewData({ valid: [], invalid: [], file: null });
              setImportStep('upload');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={formLoading || importPreviewData.valid.length === 0}
            >
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Import {importPreviewData.valid.length} Records
            </Button>
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(open) => {
        setBulkDeleteOpen(open);
        if (!open) {
          setBulkDeleteRange({ start: '', end: '' });
          setDeleteAllConfirm(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Bulk Delete Expenses
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Please proceed with caution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 border rounded-lg space-y-4">
              <p className="font-medium">Delete by Date Range</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={bulkDeleteRange.start}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setBulkDeleteRange({ ...bulkDeleteRange, start: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={bulkDeleteRange.end}
                    max={new Date().toISOString().split('T')[0]}
                    min={bulkDeleteRange.start || undefined}
                    onChange={(e) => setBulkDeleteRange({ ...bulkDeleteRange, end: e.target.value })}
                  />
                </div>
              </div>
              {/* Date range validation error */}
              {bulkDeleteRange.start && bulkDeleteRange.end && bulkDeleteRange.start > bulkDeleteRange.end && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Start date cannot be after end date
                </p>
              )}
              <Button 
                variant="destructive" 
                className="w-full"
                disabled={
                  !bulkDeleteRange.start || 
                  !bulkDeleteRange.end || 
                  bulkDeleteRange.start > bulkDeleteRange.end ||
                  formLoading
                }
                onClick={() => handleBulkDelete(false)}
              >
                Delete Range
              </Button>
            </div>
            
            <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
              <p className="font-medium text-destructive mb-4">Delete All Expenses</p>
              {!deleteAllConfirm ? (
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={() => setDeleteAllConfirm(true)}
                >
                  Delete All
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">Are you absolutely sure? This will delete ALL expenses!</p>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setDeleteAllConfirm(false)}>
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      disabled={formLoading}
                      onClick={() => handleBulkDelete(true)}
                    >
                      {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Confirm Delete All
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseList;
